// src/lib/tokens.ts
// Single source of truth for all visual decisions in GlowHire.
// Every colour, font, spacing value, border radius, shadow, and component
// style variant is defined here. Import from this file — never hardcode hex.

import type { CSSProperties } from 'react';

// ---------------------------------------------------------------------------
// Brand colours
// ---------------------------------------------------------------------------

// Neutral tokens below resolve through CSS custom properties defined in
// globals.css (`:root` for dark, `:root[data-theme="light"]` for light), so
// every consumer of `brand.*` repaints automatically when ThemeProvider
// flips the theme — no per-component changes needed. Accent-derived tokens
// stay literal since the accent colour (#68BBCB) is identical in both themes.
export const brand = {
  canvas:             'var(--color-canvas)',
  surface1:           'var(--color-surface-1)',
  surface2:           'var(--color-surface-2)',
  surface3:           'var(--color-surface-3)',
  surface4:           'var(--color-surface-4)',
  onSurface:          'var(--color-on-surface)',
  onSurfaceVariant:   'var(--color-on-surface-variant)',
  muted:              'var(--color-muted)',
  monoDim:            'var(--color-mono-dim)',
  monoDarker:         'var(--color-mono-darker)',
  hairline:           'var(--color-hairline)',
  hairlineSoft:       'var(--color-hairline-soft)',
  hairlineFull:       'rgba(var(--fg-rgb),0.14)',
  accent:             '#68BBCB',
  accentTint:         'rgba(104,187,203,0.12)',
  success:            'var(--color-success)',
  successTint:        'rgba(74,222,128,0.12)',
  warning:            'var(--color-warning)',
  warningTint:        'rgba(251,146,60,0.12)',
  error:              'var(--color-error)',
  errorTint:          'rgba(255,180,171,0.12)',
  pendingYellow:      'var(--color-warning)',
  // Canvas variants
  canvasWarm:         'var(--color-canvas-warm)',
  // Accent alpha variants
  accentFaint:        'rgba(104,187,203,0.075)',
  accentSubtle:       'rgba(104,187,203,0.18)',
  accentThin:         'rgba(104,187,203,0.2)',
  accentBorder:       'rgba(104,187,203,0.28)',
  accentMid:          'rgba(104,187,203,0.38)',
  accentStrong:       'rgba(104,187,203,0.9)',
  // Foreground alpha text variants (dark: white overlays, light: dark overlays)
  textDim:            'rgba(var(--fg-rgb),0.4)',
  textFaint:          'rgba(var(--fg-rgb),0.45)',
  textHalf:           'rgba(var(--fg-rgb),0.5)',
  textSoft:           'rgba(var(--fg-rgb),0.55)',
  textSub:            'rgba(var(--fg-rgb),0.58)',
  textStrong:         'rgba(var(--fg-rgb),0.7)',
  // Foreground alpha surface variants
  surfaceFaint:       'rgba(var(--fg-rgb),0.03)',
  surfaceDim:         'rgba(var(--fg-rgb),0.06)',
  // Hairline alpha variants (ascending opacity)
  hairlineMid:        'rgba(var(--fg-rgb),0.1)',
  hairlineStrong:     'rgba(var(--fg-rgb),0.12)',
  // Error alpha variants (ascending opacity) — fixed semantic red
  errorBgSoft:        'rgba(239,68,68,0.06)',
  errorBg:            'rgba(239,68,68,0.1)',
  errorBorder:        'rgba(239,68,68,0.12)',
  errorBorderMid:     'rgba(239,68,68,0.3)',
  errorText:          'rgba(239,68,68,0.7)',
  // Success alpha variants
  successTintSoft:    'rgba(34,197,94,0.06)',
  surfaceSubtle:      'rgba(var(--fg-rgb),0.04)',
} as const;

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const fonts = {
  sans:  'var(--font-manrope)',
  serif: 'var(--font-serif)',
  mono:  'var(--font-jetbrains-mono)',
} as const;

// ---------------------------------------------------------------------------
// Spacing scale (px)
// ---------------------------------------------------------------------------

export const spacing = {
  xs:   4,
  sm:   8,
  md:   12,
  lg:   16,
  xl:   20,
  xxl:  24,
  xxxl: 32,
} as const;

// ---------------------------------------------------------------------------
// Motion
// ---------------------------------------------------------------------------

export const motion = {
  fast:         '100ms ease',
  default:      '150ms ease',
  slow:         '300ms ease',
  overlayEnter: '220ms cubic-bezier(0.2, 0, 0, 1)',
} as const;

// ---------------------------------------------------------------------------
// Shadows
// ---------------------------------------------------------------------------

export const shadows = {
  cardResting:  '0 1px 3px rgba(0,0,0,0.6)',
  cardElevated: '0 4px 20px rgba(0,0,0,0.8)',
  accentGlow:   '0 0 20px rgba(104,187,203,0.25)',
  primaryGlow:  '0 0 15px rgba(var(--fg-rgb),0.2)',
} as const;

// ---------------------------------------------------------------------------
// Component style presets — spread these into inline style objects.
// Only colour, font, and shadow values live here; layout values stay inline.
// ---------------------------------------------------------------------------

export const styles = {
  monoLabel: {
    fontFamily:    fonts.mono,
    fontSize:      10,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color:         brand.monoDim,
  } satisfies CSSProperties,

  bodyText: {
    fontFamily: fonts.sans,
    fontSize:   14,
    color:      brand.onSurface,
    lineHeight: 1.7,
  } satisfies CSSProperties,

  bodyTextMuted: {
    fontFamily: fonts.sans,
    fontSize:   14,
    color:      brand.onSurfaceVariant,
    lineHeight: 1.7,
  } satisfies CSSProperties,

  glassCard: {
    background:   brand.surface4,
    border:       `1px solid ${brand.hairline}`,
    borderRadius: 12,
  } satisfies CSSProperties,

  inputBase: {
    background: 'var(--input-recessed-bg)',
    border:     `1px solid ${brand.hairline}`,
    color:      brand.onSurface,
    borderRadius: 6,
    fontFamily: fonts.mono,
    fontSize:   12,
    outline:    'none',
  } satisfies CSSProperties,

  ghostBtn: {
    background:  'transparent',
    border:      `1px solid ${brand.hairline}`,
    borderRadius: 8,
    color:       brand.onSurfaceVariant,
    cursor:      'pointer',
    fontFamily:  fonts.sans,
    fontSize:    12,
    padding:     '3px 10px',
    whiteSpace:  'nowrap',
  } satisfies CSSProperties,

  dangerBtn: {
    background:   brand.errorTint,
    border:       `1px solid rgba(255,180,171,0.2)`,
    borderRadius: 8,
    color:        brand.error,
    cursor:       'pointer',
    fontFamily:   fonts.sans,
    fontSize:     12,
    padding:      '4px 12px',
    whiteSpace:   'nowrap',
  } satisfies CSSProperties,

  primaryBtn: {
    background:   brand.onSurface,
    border:       'none',
    borderRadius: 8,
    color:        brand.canvas,
    cursor:       'pointer',
    fontFamily:   fonts.sans,
    fontSize:     12,
    fontWeight:   600,
    padding:      '4px 16px',
    whiteSpace:   'nowrap',
  } satisfies CSSProperties,

  iconBtn: {
    background: 'transparent',
    border:     'none',
    cursor:     'pointer',
    padding:    4,
    color:      brand.monoDim,
    display:    'inline-flex',
    alignItems: 'center',
  } satisfies CSSProperties,
} as const;

// ---------------------------------------------------------------------------
// Convenience re-export
// ---------------------------------------------------------------------------

export const tokens = { brand, fonts, spacing, motion, shadows, styles };
